import React, {
  useContext,
  useReducer,
  useEffect,
  useState,
  useMemo,
} from "react";
import { SearchBar } from "components";
import { customStylesSubsidy } from "utils/helpers/styles";
import Select from "react-select";
import { AssignTeam, AddUsersToMeal, TeamRemove, BulkUserRemove } from "alerts";
import { useManagePlan } from "contexts/ManagePlanContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft } from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MobXProviderContext } from "mobx-react";
import { useParams } from "react-router-dom";
import { toJS } from "mobx";
import { priceFormatter } from "utils/helpers/validation";
import { formattedAddres } from "utils/helpers/string";
import { NotFound } from "components";
import { isPresent } from "utils/helpers/array";
import dayjs from "dayjs";
import dateTime from "utils/helpers/dateTime";
import { PageGuide } from "components";

function ManageUsers() {
  const navigate = useNavigate();
  const store = useContext(MobXProviderContext);
  const userStore = toJS(store?.userStore);
  const homeStore = toJS(store?.homeStore);
  const { locationID } = userStore;
  const { mealId } = useParams();
  const { teams, locationEmployees } = homeStore;
  const [bulkUsers, setBulkUsers] = useState([]);
  const [subsidySelect, setSubsidySelect] = useState(null);

  const {
    removeUser,
    setRemoveUser,
    assignTeam,
    setAssignTeam,
    addUsersToMeal,
    setAddUsersToMeal,
    confirmTeamRemove,
    setConfirmTeamRemove,
  } = useManagePlan();

  const initialState = {
    meal: null,
    usersWithOrderInfo: [],
    userTeamFilter: null,
    userOrderSearchText: "",
    totalUsersUnderMeal: 0,
    usersUnderMeal: [],
    searchText: "",
  };

  function reducer(state, action) {
    switch (action.type) {
      case "reset":
        return initialState;
    }
    return {
      ...state,
      [action.field]: action.value,
    };
  }

  const [provider, dispatch] = useReducer(reducer, initialState);

  const { isPending, error, data, refetch } = useQuery({
    queryKey: ["groupOrder", locationID, mealId],
    refetchOnWindowFocus: false,
    queryFn: () => {
      return store.homeStore.fetchMealDetail(mealId, locationID);
    },
  });

  let { meal, totalUsersUnderMeal, usersUnderMeal } = provider;

  useEffect(() => {
    window.scroll({
      top: 0,
      left: 0,
    });
  }, []);

  useEffect(() => {
    if (!data) return;
    dispatch({ field: "meal", value: data });

    dispatch({
      field: "totalUsersUnderMeal",
      value: data?.attributes?.location_employees?.length,
    });

    dispatch({
      field: "usersUnderMeal",
      value: data?.attributes?.employee_meals,
    });

    store?.homeStore?.fetchTeams(locationID);
    store?.homeStore?.fetchEmployees(locationID);
  }, [data]);

  useEffect(() => {
    setBulkUsers([]);

    if (subsidySelect?.value === "default") {
      let locEmployeesCopy = meal?.attributes?.employee_meals;

      locEmployeesCopy = locEmployeesCopy?.filter(
        (user) => user?.subsidy_type == null
      );

      dispatch({
        field: "usersUnderMeal",
        value: locEmployeesCopy,
      });
    } else if (subsidySelect?.value === "custom") {
      let locEmployeesCopy = meal?.attributes?.employee_meals;
      locEmployeesCopy = locEmployeesCopy?.filter(
        (user) => user?.subsidy_type != null
      );
      dispatch({
        field: "usersUnderMeal",
        value: locEmployeesCopy,
      });
    } else {
      dispatch({
        field: "usersUnderMeal",
        value: meal?.attributes?.employee_meals,
      });
    }
  }, [subsidySelect, meal]);

  const onEmployeeChecked = (e, person) => {
    if (e.target.checked) {
      let locationEmployees = meal?.attributes?.location_employees;

      let found = locationEmployees?.find(
        (employee) => person.location_employee_id == employee?.id
      );

      let isPresent = bulkUsers?.find((user) => {
        return user?.id == person?.location_employee_id;
      });

      if (isPresent) {
        let filtered = bulkUsers?.filter((user) => {
          return user?.id != person?.location_employee_id;
        });

        setBulkUsers(filtered);
      } else {
        setBulkUsers([
          ...bulkUsers,
          { id: found?.id, employee_id: person?.id },
        ]);
      }
    } else {
      let filtered = bulkUsers?.filter((user) => {
        return user?.id != person?.location_employee_id;
      });

      setBulkUsers(filtered);
    }
  };

  const handleOnChange = (e) => {
    dispatch({ field: "searchText", value: e?.target?.value });
  };

  useMemo(() => {
    const matchesSearchTerm = (value) =>
      provider.searchText
        ? value?.toLowerCase().includes(provider.searchText?.toLowerCase())
        : true;

    const users = data?.attributes?.employee_meals;

    const filteredUsers = users?.filter((user) => {
      const { first_name, last_name, email } = user;

      const matchesSearchText = provider.searchText
        ? matchesSearchTerm(first_name) ||
          matchesSearchTerm(last_name) ||
          matchesSearchTerm(email)
        : true;

      return matchesSearchText;
    });

    if (provider.searchText == "") {
      dispatch({
        field: "usersUnderMeal",
        value: data?.attributes?.employee_meals,
      });
    } else dispatch({ field: "usersUnderMeal", value: filteredUsers });
  }, [provider.searchText]);

  return (
    <div className="d-col p-6 w-full relative">
      <div className="d-row items-start justify-between">
        <button
          className="flex items-center cursor-pointer mb-[24px]"
          onClick={() => {
            navigate(-1);
          }}
        >
          <FontAwesomeIcon
            icon={faAngleLeft}
            style={{ width: 14, height: 14 }}
          />
          <span className="text-md text-left font-inter-medium text-black-light ml-2">
            Back
          </span>
        </button>

        <PageGuide />
      </div>

      <div className="d-row w-full justify-center mt-[18px]">
        <small className="font-inter-medium mr-5 text-dark-gray text-[14px] self-center">
          {dayjs(meal?.attributes?.meal_date).format(dateTime.longDateWithWeek)}
        </small>

        <small className="font-inter-medium text-dark-gray text-[14px] self-center">
          Delivery Address:{" "}
          {formattedAddres(meal?.attributes?.address?.attributes)}
        </small>
      </div>

      <h2 className="font-inter-semibold text-dark-gray text-[24px] mt-[5px] mb-[45px] self-center">
        Changes to {meal?.attributes?.name}
      </h2>

      <div>
        <div className="d-row mt-[24px] items-center justify-between">
          <strong className="text-dark-gray text-[20px] font-inter-semibold">
            Teams in {meal?.attributes?.name}
          </strong>
          <button
            className="btn-blue-accent"
            onClick={() => {
              refetch();
              setAssignTeam(true);
            }}
          >
            Assign teams
          </button>
        </div>

        <p className="font-inter-regular max-w-[543px] text-[14px] text-dark-gray">
          Users added or removed from the following teams will be automatically
          added or removed from this meal
        </p>

        <div className="flex items-center space-x-2 mt-[24px]">
          {meal?.attributes?.teams?.map((team) => (
            <div
              key={team?.id}
              className="bg-light-gray px-4 py-[12px] rounded-full flex items-center"
            >
              <span className="text-sm text-gray-700">{team?.name}</span>
              <button
                className="ml-2 text-gray-700 text-xs"
                onClick={() => {
                  refetch();
                  setConfirmTeamRemove(team?.id);
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        <NotFound data={meal?.attributes?.teams} message="No teams found" />
      </div>

      <strong className="text-dark-gray text-[20px] font-inter-semibold mt-[24px]">
        Users in this meal
      </strong>

      <span className="font-inter-regular max-w-[543px] text-[14px] text-dark-gray">
        Users number: {totalUsersUnderMeal}
      </span>

      <div className="d-row items-center justify-between mt-[24px]">
        <div className="d-row items-center">
          <SearchBar
            width="w-[360px]"
            placeholder="Search"
            handleOnChange={handleOnChange}
            value={provider.searchText}
          />

          <small className="fonte-inter-regular tex-sm mx-[10px]">
            Filter by:
          </small>

          <Select
            styles={customStylesSubsidy({
              preferences: true,
              noBorder: true,
            })}
            aria-labelledby="sort-by"
            aria-label="sort-by"
            placeholder="Subsidy type"
            closeMenuOnSelect
            isClearable
            onChange={(option) => setSubsidySelect(option)}
            options={[
              { label: "Custom", value: "custom" },
              { label: "Default", value: "default" },
            ]}
            className="w-[195px]"
          />
        </div>

        <button
          className="btn-blue-accent"
          onClick={() => {
            refetch();
            setAddUsersToMeal(true);
          }}
        >
          Add users to this meal
        </button>
      </div>

      <div className="mt-[13px] flow-root">
        <div className="flex h-96 border-separate flex-col overflow-clip border-solid">
          <table className="w-full table-fixed">
            <thead className="sticky top-0 bg-light-gray">
              <tr>
                <th className="p-2"></th>
                <th className="p-2 font-inter-medium text-[14px] text-left">
                  First name
                </th>
                <th className="p-2 font-inter-medium text-[14px] text-left">
                  Last name
                </th>
                <th className="p-2 font-inter-medium text-[14px] text-left">
                  Email
                </th>
                <th className="p-2 font-inter-medium text-[14px] text-left">
                  Team
                </th>
                <th className="p-2 font-inter-medium text-[14px] text-left">
                  Subsidy
                </th>
                <th className="p-2 font-inter-medium text-[14px] text-left">
                  Subsidy Value
                </th>
              </tr>
            </thead>
          </table>

          <div className="flex-1 overflow-y-auto">
            <table className="w-full table-fixed">
              <tbody>
                {usersUnderMeal?.map((person) => (
                  <tr
                    key={person.email}
                    className="even:bg-light-gray space-x-2"
                  >
                    <td className="py-3.5 px-2">
                      <label className="inline-flex items-center">
                        <input
                          type="checkbox"
                          key={meal}
                          className="form-checkbox text-blue-600 h-4 w-4"
                          onChange={(e) => {
                            onEmployeeChecked(e, person);
                          }}
                        />
                      </label>
                    </td>
                    <td className="py-3.5 px-2 text-left text-sm font-inter-regular text-dark-gray sm:pl-3">
                      {person?.first_name}
                    </td>
                    <td className="py-3.5 px-2 text-left text-sm font-inter-regular text-dark-gray sm:pl-3">
                      {person?.last_name}
                    </td>
                    <td className="whitespace-normal break-words py-3.5 px-2 text-left text-sm font-inter-regular text-dark-gray sm:pl-3">
                      {person?.email}
                    </td>
                    <td className="py-3.5 px-2 text-left text-sm font-inter-regular text-dark-gray sm:pl-3">
                      {person?.teams?.map((team, ind, arr) => {
                        return (
                          team?.name + (ind == arr?.length - 1 ? "" : ", ")
                        );
                      })}
                    </td>
                    <td className="py-3.5 px-2 text-left text-sm font-inter-regular text-dark-gray sm:pl-3">
                      {person?.subsidy_type == null
                        ? "Meal Plan Default"
                        : "Custom"}
                    </td>

                    <td className="whitespace-normal break-words py-3.5 px-2">
                      {person?.subsidy ? (
                        <small className="font-inter-medium text-[14px] text-dark-gray">
                          {priceFormatter(person?.subsidy)}
                        </small>
                      ) : (
                        <>
                          {meal?.attributes?.subsidy ? (
                            <small className="font-inter-medium text-[14px] text-dark-gray">
                              {priceFormatter(meal?.attributes?.subsidy)}
                            </small>
                          ) : meal?.attributes?.meal_series_subsidy_type ==
                            "weekly" ? (
                            <small className="font-inter-medium text-[14px] text-dark-gray">
                              {priceFormatter(
                                meal?.attributes?.meal_series_subsidy
                              )}{" "}
                              weekly
                            </small>
                          ) : meal?.attributes?.meal_series_subsidy_type ==
                            "daily" ? (
                            <div className="grid grid-cols-3">
                              {meal?.attributes?.meal_series_daily_subsidy &&
                                Object.keys(
                                  meal?.attributes?.meal_series_daily_subsidy
                                )?.map((day) => (
                                  <div key={day} className="d-col items-center">
                                    <small className="mr-2 font-inter-medium text-dark-gray capitalize text-[7px]">
                                      {day?.substring(0, 3)}
                                    </small>
                                    <div className="bg-white items-center justify-center capitalize px-[7px] py-[3px] d-row rounded">
                                      <small className="font-inter-medium text-dark-gray text-[7px]">
                                        {priceFormatter(
                                          meal?.attributes
                                            ?.meal_series_daily_subsidy[day]
                                        )}
                                      </small>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          ) : (
                            <small className="font-inter-medium text-[14px] text-dark-gray">
                              {priceFormatter(
                                meal?.attributes?.meal_series_standard_subsidy
                              )}{" "}
                              weekly
                            </small>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <NotFound data={usersUnderMeal} message="No users found" />
          </div>
        </div>
      </div>

      {isPresent(bulkUsers) && (
        <div className="absolute bottom-0 right-0 d-row items-center justify-center left-0 ">
          <div className="btn-blue-accent rounded-3xl px-[32px] py-[16px] d-row items-center hover:bg-catering h-[48px] ">
            <small className="font-inter-medium text-[16px]">
              {bulkUsers?.length} user selected
            </small>

            <button
              className="font-inter-medium ml-2 text-white underline text-[16px] py-0"
              onClick={() => {
                setRemoveUser(true);
              }}
            >
              Remove user from this meal
            </button>
          </div>
        </div>
      )}

      {assignTeam && (
        <AssignTeam
          fromMeal={true}
          meal={meal}
          teams={teams?.filter(
            (team) =>
              !meal?.attributes?.teams?.some((item) => item?.id == team?.id)
          )}
          visible={assignTeam}
          setModal={setAssignTeam}
          handleApplyPressed={() => refetch()}
        />
      )}

      {addUsersToMeal && (
        <AddUsersToMeal
          visible={addUsersToMeal}
          setModal={setAddUsersToMeal}
          users={locationEmployees?.filter(
            (user) =>
              !meal?.attributes?.location_employees?.some(
                (item) => item?.id == user?.id
              )
          )}
          meal={meal}
          fromMeal={true}
          handleApplyPressed={() => refetch()}
        />
      )}

      {confirmTeamRemove && (
        <TeamRemove
          visible={confirmTeamRemove}
          setModal={setConfirmTeamRemove}
          fromMeal={true}
          meal={meal}
          handleApplyPressed={() => refetch()}
        />
      )}

      {removeUser && (
        <BulkUserRemove
          visible={removeUser}
          setModal={setRemoveUser}
          fromMeal={true}
          meal={meal}
          bulkUsers={bulkUsers}
          onComplete={() => {
            setBulkUsers([]);
          }}
          handleApplyPressed={() => refetch()}
        />
      )}
    </div>
  );
}

export default ManageUsers;
